import React from "react";
import { Field, Label, Switch, Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { SessionMetrics } from "./SessionMetrics";
import {
  LinkButton,
  PrimaryButtonSmall,
  SecondaryButtonSmall,
} from "../components/Button";
import { Waitlist } from "./Waitlist";
import Loader from "../components/Loader";
import { RemovedList } from "./RemovedList";
import MutualMatchQueueTable from "../../src/components/MutualMatchQueueTable";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useQueueSection } from "./useQueueSection";
import { groupHasUsers } from "./queueSectionHelpers";

const EndEventDropdown = ({ stopSession, buttonLoading, finishEvent }) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <MenuButton
            disabled={buttonLoading}
            className="inline-flex items-center bg-red-500 text-yellow-50 border border-red-500 py-2 px-4 rounded disabled:opacity-50 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            End Event
            <ChevronDownIcon
              className={`ml-2 -mr-1 h-5 w-5 transition-transform ${open ? 'transform rotate-180' : ''}`}
              aria-hidden="true"
            />
          </MenuButton>

          <MenuItems className="absolute right-0 bottom-full z-10 mb-2 w-44 origin-bottom-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={stopSession}
                    disabled={buttonLoading}
                    className={`${focus ? 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300' : 'text-red-600 dark:text-red-400'
                      } block w-full px-4 py-2 text-left text-sm disabled:opacity-50`}
                  >
                    Stop & Reset
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={finishEvent}
                    disabled={buttonLoading}
                    className={`${focus ? 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300' : 'text-red-600 dark:text-red-400'
                      } block w-full px-4 py-2 text-left text-sm disabled:opacity-50`}
                  >
                    Finish & Complete
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </>
      )}
    </Menu>
  );
};

export const QueueSection = () => {
  const {
    eventData,
    loading,
    eventUsersProfile,
    lateComers,
    removedUsers,
    pendingRemovedUsers,
    newQueue,
    editEnabled,
    setEditEnabled,
    buttonLoading,
    savingQueue,
    queueLength,
    event,
    formatRoundMetric,
    convertSeconds,
    startSession,
    stopSession,
    finishEvent,
    handleRegenerateQueue,
    handleGenerateQueue,
    deleteQueue,
    onRemoveFromQueue,
    saveQueue,
    queueUnsaved,
    showStartButton,
    hasNewConsents,
    increaseRound,
    decreaseRound,
    increaseTimeLeft,
    decreaseTimeLeft,
  } = useQueueSection();

  if (loading) {
    return (
      <div>
        <Loader></Loader>
      </div>
    );
  }

  return (
    <div className={`${eventData.isCompleted ? "opacity-50" : ""}`}>
      <UserLists
        lateComers={lateComers}
        removedUsers={removedUsers}
        pendingRemovedUsers={pendingRemovedUsers}
        eventUsersProfile={eventUsersProfile}
        groupHasUsers={groupHasUsers}
        eventData={eventData}
        movers={eventData.movers}
        sitters={eventData.sitters}
      />

      {event.activeSession && Object.keys(event.activeSession).length !== 0 && event.activeSession?.at && (
        <div>
          {event.activeSession?.currentRound >= 0 && (
            <EditSwitch editEnabled={editEnabled} setEditEnabled={setEditEnabled} />
          )}
          <div className="justify-evenly grid grid-cols-2 gap-4">
            <SessionMetrics
              editEnabled={editEnabled}
              name={`Round`}
              metric={event.activeSession?.currentRound}
              increaseMetric={increaseRound}
              decreaseMetric={decreaseRound}
              formatMetric={(metric) => formatRoundMetric(metric, queueLength, eventData)}
              loading={loading}
            />
            <SessionMetrics
              editEnabled={editEnabled}
              name={"Time"}
              metric={event.activeSession?.timeLeft}
              increaseMetric={increaseTimeLeft}
              decreaseMetric={decreaseTimeLeft}
              formatMetric={convertSeconds}
              loading={loading}
              renderEverySec={true}
              reRenderOnThisValueChange={event.activeSession?.at}
            />
          </div>
        </div>
      )}

      <QueueDisplay event={event} eventData={eventData} newQueue={newQueue} onRemoveFromQueue={onRemoveFromQueue} />

      <div className="mt-6 md:flex items-center justify-between">
        {!queueUnsaved && event.currentQueue ? (
          <LinkButton onClick={deleteQueue}>Delete queue</LinkButton>
        ) : (
          <p></p>
        )}

        <div className="flex items-center gap-x-6 mt-2 lg:mt-0">
          <QueueControls
            queueUnsaved={queueUnsaved}
            event={event}
            handleRegenerateQueue={handleRegenerateQueue}
            handleGenerateQueue={handleGenerateQueue}
            hasNewConsents={hasNewConsents}
            eventData={eventData}
            saveQueue={saveQueue}
            savingQueue={savingQueue}
          />
          <SessionControls
            event={event}
            startSession={startSession}
            stopSession={stopSession}
            finishEvent={finishEvent}
            buttonLoading={buttonLoading}
            eventData={eventData}
            showStartButton={showStartButton}
          />
        </div>
      </div>
    </div>
  );
};

// Queue Management Components
const QueueControls = ({ queueUnsaved, event, handleRegenerateQueue, handleGenerateQueue, hasNewConsents, eventData, saveQueue, savingQueue }) => (
  <div className="flex items-center gap-x-6  lg:mt-0">
    {queueUnsaved && event.currentQueue && (
      <div className="relative">
        <SecondaryButtonSmall onClick={saveQueue} disabled={savingQueue}>
          {savingQueue ? (
            <div className="flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Saving...</span>
            </div>
          ) : (
            "Save new queue"
          )}
        </SecondaryButtonSmall>
      </div>
    )}

    {!eventData.isCompleted && (
      <SecondaryButtonSmall
        onClick={event.currentQueue && eventData.activeSession?.taskID ? handleRegenerateQueue : handleGenerateQueue}
      >
        <div className="flex justify-around gap-1">
          {event.currentQueue && eventData.activeSession?.taskID ? "Re-generate queue" : "Generate queue"}
          {hasNewConsents && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
            </span>
          )}
        </div>
      </SecondaryButtonSmall>
    )}
  </div>
);

const SessionControls = ({ event, startSession, stopSession, finishEvent, buttonLoading, eventData, showStartButton }) => (
  <>
    {showStartButton && (
      <PrimaryButtonSmall onClick={startSession} loading={buttonLoading}>
        Start Event Session
      </PrimaryButtonSmall>
    )}
    {event.currentQueue && event.activeSession?.taskID && !eventData.isCompleted && (
      <EndEventDropdown
        stopSession={stopSession}
        finishEvent={finishEvent}
        buttonLoading={buttonLoading}
      />
    )}
    {event.currentQueue && event.activeSession?.taskID && eventData.isCompleted && (
      <p className="text-gray-400 dark:text-gray-500 text-center">This event has ended</p>
    )}
  </>
);

const EditSwitch = ({ editEnabled, setEditEnabled }) => (
  <Field className="flex items-center justify-end mb-2">
    <Label as="span" className="mr-2 text-sm">
      <span className="text-gray-500 dark:text-gray-400 text-sm">Edit</span>
    </Label>
    <Switch
      checked={editEnabled}
      onChange={setEditEnabled}
      className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-primary dark:data-[checked]:bg-primary"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
      />
    </Switch>
  </Field>
);

const QueueDisplay = ({ event, eventData, newQueue, onRemoveFromQueue }) => (
  <>
    {event.currentQueue && Object.keys(event.currentQueue).length !== 0 ? (
      <div className={`mt-6`}>
        <MutualMatchQueueTable
          activeSession={event.activeSession}
          currentQueue={event.currentQueue}
          eventUsers={Object.values(eventData.eventUsers)}
          groupCount={eventData.groupCount}
          onRemoveFromQueue={onRemoveFromQueue}
        />
      </div>
    ) : (
      <p className="text-gray-400 dark:text-gray-500 mt-3">No queue exists</p>
    )}
    {newQueue && (
      <div className={`mt-6`}>
        <h1 className="text-lg font-bold mb-4">New Queue</h1>
        <MutualMatchQueueTable
          activeSession={event.activeSession}
          currentQueue={newQueue}
          eventUsers={Object.values(eventData.eventUsers)}
          groupCount={eventData.groupCount}
        />
      </div>
    )}
  </>
);

const UserLists = ({ lateComers, removedUsers, pendingRemovedUsers, eventUsersProfile, groupHasUsers, eventData, movers, sitters }) => (
  <div className="grid lg:grid-cols-2 gap-4">
    {groupHasUsers(lateComers) && (
      <div className="mb-4">
        <h1 className="w-full text-center my-2 text-gray-500 dark:text-gray-400">
          Waiting to Join...
        </h1>
        <Waitlist lateComers={lateComers} eventUsersProfile={eventUsersProfile} eventUsers={eventData?.eventUsers} />
      </div>
    )}
    {(groupHasUsers(removedUsers) || pendingRemovedUsers?.length > 0) && (
      <div className="mb-4">
        <h1 className="w-full text-center my-2 text-gray-500 dark:text-gray-400">
          Removed users
        </h1>
        <RemovedList
          removedUsers={removedUsers}
          pendingRemovedUsers={pendingRemovedUsers}
          eventUsers={eventData?.eventUsers}
          movers={movers}
          sitters={sitters}
        />
      </div>
    )}
  </div>
);
